import { useEffect, useRef } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import Content from "../components/Content";
import {
  FEATURE_TOGGLES,
  type FeatureToggleId,
  useFeatureToggle,
  writeFeatureToggle,
} from "../lib/featureToggles";

const FeatureToggleSwitch = ({
  id,
  name,
}: {
  id: FeatureToggleId;
  name: string;
}) => {
  const on = useFeatureToggle(id);
  return (
    <Form.Check
      type="switch"
      role="switch"
      id={`feature-toggle-${id}`}
      label={name}
      checked={on}
      // Writes straight to this browser: the profile form's Save is not involved.
      onChange={(e) => writeFeatureToggle(id, e.target.checked)}
    />
  );
};

/**
 * The feature toggles of this browser, shown only at /profile#toggles. Every
 * signed-in user sees it; there is no role check. It scrolls into view once
 * `settled` (the profile read has finished, whether or not it succeeded).
 */
const FeatureToggles = ({ settled }: { settled: boolean }) => {
  const { hash } = useLocation();
  const show = hash === "#toggles";
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    // Wait for the profile read to settle: the form renders above the section
    // and would push it out of view.
    if (!show || !settled) return;
    // jsdom has no scrollIntoView.
    sectionRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [show, settled]);

  if (!show) return null;
  return (
    <section id="toggles" ref={sectionRef} aria-labelledby="toggles-heading">
      <Row>
        <Col>
          <h2 id="toggles-heading">Feature toggles</h2>
        </Col>
      </Row>
      <Content>
        {FEATURE_TOGGLES.map((toggle) => (
          <FeatureToggleSwitch
            key={toggle.id}
            id={toggle.id}
            name={toggle.name}
          />
        ))}
      </Content>
    </section>
  );
};

export default FeatureToggles;
